import { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { clsx } from 'clsx';
import { Button, Input, Select } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useDebounce } from '@/shared/hooks';
import type { CarrierResolution, PayerDirectoryEntry } from '../api/verificationApi';
import { usePayerSearch } from '../hooks/useImportFlow';
import { pluralize } from '../utils/format';
import styles from './ResolveCarriersStep.module.css';

interface ResolveCarriersStepProps {
  carriers: CarrierResolution[];
  onSelect: (carrierNameRaw: string, payer: PayerDirectoryEntry | null) => void;
  onBack: () => void;
  onContinue: () => void;
  saving: boolean;
}

function CarrierRow({
  carrier,
  onSelect,
}: {
  carrier: CarrierResolution;
  onSelect: (payer: PayerDirectoryEntry | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 300);
  const { results, isFetching } = usePayerSearch(debounced);

  // Suggestions first, then search hits not already listed.
  const options = useMemo(() => {
    const seen = new Set<string>();
    const merged: PayerDirectoryEntry[] = [];
    for (const p of [...carrier.suggestions, ...results]) {
      if (seen.has(p.payerId)) continue;
      seen.add(p.payerId);
      merged.push(p);
    }
    return merged;
  }, [carrier.suggestions, results]);

  const matched = !!carrier.payerId && !editing;

  return (
    <div className={styles.row}>
      <div className={styles.name}>
        {carrier.carrierNameRaw}
        <div className={styles.count}>{pluralize(carrier.count, 'appointment')}</div>
      </div>

      {matched ? (
        <div className={styles.matched}>
          <span className={clsx(patterns.pillSuccess, styles.badge)}>
            <Check size={11} strokeWidth={3} aria-hidden />
            {carrier.payerName}
          </span>
          <button type="button" className={styles.link} onClick={() => setEditing(true)}>
            change
          </button>
        </div>
      ) : (
        <div className={styles.picker}>
          <Select
            aria-label={`Payer for ${carrier.carrierNameRaw}`}
            placeholder="Select payer…"
            options={options.map((p) => ({ value: p.payerId, label: p.name }))}
            value={carrier.payerId ?? ''}
            onChange={(e) => {
              const payer = options.find((p) => p.payerId === e.target.value) ?? null;
              onSelect(payer);
              if (payer) setEditing(false);
            }}
            fullWidth
          />
          <Input
            aria-label={`Search payers for ${carrier.carrierNameRaw}`}
            placeholder="Search the payer directory…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search size={14} aria-hidden />}
            hint={
              isFetching
                ? 'Searching…'
                : debounced.trim().length >= 2 && results.length === 0
                  ? 'No payers match — try a shorter name'
                  : undefined
            }
            fullWidth
          />
        </div>
      )}
    </div>
  );
}

export function ResolveCarriersStep({
  carriers,
  onSelect,
  onBack,
  onContinue,
  saving,
}: ResolveCarriersStepProps) {
  const unmatched = carriers.filter((c) => !c.payerId).length;

  return (
    <div className={styles.root}>
      <p className={patterns.pageSubtitle}>
        Match each carrier name in your file to a payer we can check electronically. We remember
        these matches for next time.
      </p>

      <div className={clsx(patterns.panel, styles.list)}>
        {carriers.length === 0 && (
          <div className={patterns.gridTableEmpty}>
            No carriers found in the file — check the carrier column mapping.
          </div>
        )}
        {carriers.map((c) => (
          <CarrierRow
            key={c.carrierNameRaw}
            carrier={c}
            onSelect={(payer) => onSelect(c.carrierNameRaw, payer)}
          />
        ))}
      </div>

      <div className={styles.note}>
        {unmatched > 0 ? (
          <>
            <strong>{pluralize(unmatched, 'carrier needs', 'carriers need')} a match.</strong>{' '}
            Medicaid MCOs vary by state — pick the specific plan so the check routes correctly. You
            can continue without a match; those rows import flagged "Needs payer".
          </>
        ) : (
          <>Every carrier has a payer match. Continue to preview the rows.</>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant="ghost" onClick={onBack} disabled={saving}>
          Back
        </Button>
        <Button onClick={onContinue} isLoading={saving}>
          Continue
        </Button>
      </div>
    </div>
  );
}
